import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Coins, Check, Search } from 'lucide-react';
import { formatCurrency } from '../utils/format';

const AppSelector = ({ apps, selectedAppUuid, onSelect, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const selectedApp = apps.find(app => app.uuid === selectedAppUuid);
  const filteredApps = apps.filter(app => 
    app.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (app) => {
    onSelect(app.uuid);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-4 py-3 border-2 border-purple-200 dark:border-purple-800 rounded-lg bg-white dark:bg-gray-800 hover:border-purple-400 dark:hover:border-purple-600 transition-all flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {selectedApp ? (
          <div className="flex items-center gap-3 flex-1">
            {selectedApp.logo_url && (
              <img 
                src={selectedApp.logo_url} 
                alt={selectedApp.name}
                className="w-10 h-10 object-contain rounded"
              />
            )}
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">{selectedApp.name}</p>
              <div className="flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400">
                <Coins size={14} />
                <span className="font-medium">{formatCurrency(selectedApp.value)} créditos</span>
              </div>
            </div>
          </div>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Selecione um aplicativo...</span>
        )}
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed z-[9999] bg-white border-2 border-purple-200 rounded-lg shadow-2xl max-h-96 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          style={{
            left: dropdownRef.current?.getBoundingClientRect().left,
            top: dropdownRef.current?.getBoundingClientRect().bottom + 8,
            width: dropdownRef.current?.getBoundingClientRect().width,
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar aplicativo..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Apps List - Max 5 items visible */}
          <div className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: '300px' }}>
            {filteredApps.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">Nenhum app encontrado</p>
            ) : (
              filteredApps.map((app) => (
              <button
                key={app.uuid}
                type="button"
                onClick={() => handleSelect(app)}
                className={`w-full px-3 py-3 rounded-lg flex items-center gap-3 transition-all hover:bg-purple-50 ${
                  selectedAppUuid === app.uuid ? 'bg-purple-100 ring-2 ring-purple-500' : ''
                }`}
              >
                {app.logo_url && (
                  <img 
                    src={app.logo_url} 
                    alt={app.name}
                    className="w-10 h-10 object-contain rounded flex-shrink-0"
                  />
                )}
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">{app.name}</p>
                  <div className="flex items-center gap-1 text-sm text-purple-600">
                    <Coins size={12} />
                    <span>{formatCurrency(app.value)} créditos</span>
                  </div>
                </div>
                {selectedAppUuid === app.uuid && (
                  <Check size={20} className="text-purple-600 flex-shrink-0" />
                )}
              </button>
            ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSelector;
